/**
 * useQuery hook for fetching a single story or template.
 *
 * Replaces `storyViewEffects.getStory` (compose-app). Calls
 * `getStoryOrTemplateById` — same transport layer as the old effect.
 *
 * Auth: cookie/session (non-admin reachable) via the stories GraphQL proxy.
 *
 * Pass `null` to disable during route transitions before `entityId > 0` (§9).
 */

import { useQuery } from '@tanstack/react-query';
import { IStory } from '@opengov/stories-shared/types';

import { getStoryOrTemplateById } from '../../../routes/hasura';
import { useToast } from '../../Toast/context/useToast.context';
import { storyViewQueryKeys, StoryKeyParams } from './storyViewQueryKeys';

export type UseStoryParams = StoryKeyParams | null;

export function useStory(params: UseStoryParams, errorMessage: string) {
  const { toggleToast } = useToast();

  return useQuery<IStory, Error>({
    queryKey: params
      ? storyViewQueryKeys.story(params)
      : ([...storyViewQueryKeys.all, 'story', 'disabled'] as const),
    queryFn: async () => {
      if (!params) throw new Error('Disabled');
      const { storyId, isTemplate } = params;
      return getStoryOrTemplateById(isTemplate, storyId);
    },
    enabled: params !== null,
    onError: () => {
      toggleToast({ title: errorMessage, severity: 'error' });
    },
  });
}
